<template>
  <StepCard
    step-id="set_up_time_limits"
    :description="t('description')"
    :edit-label="t('edit')"
    :completed
    :errors="serverErrors"
    :saving
    :save-label="t('save_limits')"
    :save-hint
    :form="formId"
    @skip="$emit('skip')"
    @cancel="$emit('cancel')"
  >
    <template #summary>
      <ZoneTable
        :columns="summaryColumns"
        :rows="summaryRows"
        :toolbar="false"
        :paginated="false"
        sticky-header
        :label="t('step_names.set_up_time_limits')"
        data-testid="limits-summary"
      >
        <template #cell-name="{ row }">
          <span
            v-if="row.id === 'all'"
            class="flex items-center gap-3 font-regular text-gray-900"
          >
            <span class="flex size-8 items-center justify-center rounded-full bg-primary-100">
              <ZoneIcon
                icon="groups"
                class="text-primary-800"
                aria-hidden="true"
              />
            </span>
            <span class="font-semibold">{{ row.name }}</span>
          </span>
          <ZoneAvatar
            v-else
            :src="row.avatar"
            :name="row.name"
            :text="row.name"
            alt=""
          />
        </template>
        <template
          v-for="field in LIMIT_FIELDS"
          :key="field"
          #[`cell-${field}`]="{ value }"
        >
          <span
            class="whitespace-nowrap"
            :class="value.muted ? 'text-gray-500' : 'text-gray-900'"
          >{{ value.text }}</span>
        </template>
      </ZoneTable>
    </template>

    <form
      :id="formId"
      class="flex flex-col gap-4"
      novalidate
      @submit="save"
    >
      <div class="flex flex-col gap-1.5">
        <div class="flex items-center gap-5 px-3">
          <p class="w-36 shrink-0 text-xs font-semibold uppercase text-gray-600">{{ t('global_limits') }}</p>
          <p
            v-for="field in LIMIT_FIELDS"
            :key="field"
            class="flex-1 text-xs font-medium uppercase text-gray-600"
          >
            {{ t(field) }}
          </p>
        </div>
        <div class="flex items-start gap-5 px-3">
          <div class="flex h-10 w-36 shrink-0 items-center gap-1">
            <span class="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-100">
              <ZoneIcon
                icon="groups"
                class="text-base! text-primary-800"
                aria-hidden="true"
              />
            </span>
            <span class="truncate text-xs font-medium text-gray-900">{{ t('all_members') }}</span>
          </div>
          <div
            v-for="field in LIMIT_FIELDS"
            :key="field"
            class="flex min-w-0 flex-1 flex-col gap-1"
          >
            <div class="relative">
              <ZoneTextInput
                v-model="models[`global.${field}`].value"
                inputmode="decimal"
                :invalid="!!errors[`global.${field}`]"
                class="pr-12"
                :aria-label="t(field)"
                :data-testid="`limits-global-${field}`"
                @keydown.enter.prevent
              />
              <span
                class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 whitespace-nowrap text-xs text-gray-400"
              >{{ t(`${field}_unit`) }}</span>
            </div>
            <p
              v-if="errors[`global.${field}`]"
              class="text-xs text-red-600"
              role="alert"
              :data-testid="`limits-global-${field}-error`"
            >
              {{ errors[`global.${field}`] }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="rounded-lg border border-primary-700 bg-primary-50 p-2"
        role="status"
      >
        <p class="text-sm text-primary-800">{{ t('info') }}</p>
      </div>

      <div class="flex flex-col gap-1.5">
        <p class="px-3 text-xs font-semibold uppercase text-gray-600">{{ t('individual_limits') }}</p>
        <div class="flex flex-col gap-2.5 rounded-lg border border-gray-200 p-3">
          <div
            v-for="(member, index) in values.members"
            :key="member.id"
            class="flex items-start gap-5"
          >
            <div class="flex h-10 w-36 shrink-0 items-center gap-1.5">
              <ZoneAvatar
                :src="member.avatar"
                :name="member.name"
                alt=""
              />
              <span class="truncate text-xs font-medium text-gray-900">{{ member.name }}</span>
            </div>
            <div
              v-for="field in LIMIT_FIELDS"
              :key="field"
              class="flex min-w-0 flex-1 flex-col gap-1"
            >
              <div class="relative">
                <ZoneTextInput
                  v-model="models[`members[${index}].${field}`].value"
                  inputmode="decimal"
                  :placeholder="String(values.global[field] ?? '')"
                  :invalid="!!errors[`members[${index}].${field}`]"
                  class="pr-12"
                  :aria-label="t('member_limit', { name: member.name, limit: t(field) })"
                  :data-testid="`limits-member-${member.id}-${field}`"
                  @keydown.enter.prevent
                />
                <span
                  class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 whitespace-nowrap text-xs text-gray-400"
                >{{ t(`${field}_unit`) }}</span>
              </div>
              <p
                v-if="errors[`members[${index}].${field}`]"
                class="text-xs text-red-600"
                role="alert"
                :data-testid="`limits-member-${member.id}-${field}-error`"
              >
                {{ errors[`members[${index}].${field}`] }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </form>
  </StepCard>
</template>

<script setup>
import { useForm } from 'vee-validate';
import { computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import StepCard from '~/components/experiments/0239_getting_started_checklist/StepCard.vue';
import { useStepSave } from '~/components/experiments/0239_getting_started_checklist/useStepSave';
import { OrganizationsResource, UserOrganizationsResource } from '~/resources';
import { SUMMARY_COLUMN, SUMMARY_VALUE_COLUMN } from '~/components/experiments/0239_getting_started_checklist/summaryTable';
import ZoneAvatar from 'zone/components/base/Avatar.vue';
import ZoneIcon from 'zone/components/base/Icon.vue';
import ZoneTable from 'zone/components/base/Table.vue';
import ZoneTextInput from 'zone/components/base/TextInput.vue';
import { chain, maxValue, numeric, positive } from '~/zone_inertia/validations';
import { MAX_DAILY_LIMIT, MAX_WEEKLY_LIMIT } from '~/app_constants';

const LIMIT_FIELDS = ['weekly_limit', 'daily_limit'];
const DEFAULT_LIMITS = { weekly_limit: 40, daily_limit: 8 };
const MAX_HOURS = { weekly_limit: MAX_WEEKLY_LIMIT, daily_limit: MAX_DAILY_LIMIT };

const { data, completed, organization } = defineProps({
  data: { type: Object, required: true },
  completed: { type: Boolean },
  organization: { type: Object, required: true },
});

const emit = defineEmits(['saved', 'skip', 'cancel']);

const { t } = useI18n();
const { saving, serverErrors, failures, finishStep, run } = useStepSave(emit);

const blank = (value) => value === '' || value == null;
const limitRules = Object.fromEntries(LIMIT_FIELDS.map((field) => [field, chain([numeric(), positive(), maxValue(MAX_HOURS[field])])]));

const formId = useId();

const validationSchema = Object.fromEntries(LIMIT_FIELDS.flatMap((field) => [
  [`global.${field}`, limitRules[field]],
  ...data.members.map((_, index) => [`members[${index}].${field}`, limitRules[field]]),
]));

const { values, errors, meta, defineField, handleSubmit } = useForm({
  initialValues: {
    global: Object.fromEntries(LIMIT_FIELDS.map((field) => [
      field,
      blank(data.global_limits[field]) ? DEFAULT_LIMITS[field] : data.global_limits[field],
    ])),
    members: data.members.map((member) => ({ ...member })),
  },
  validationSchema,
});

const models = Object.fromEntries(Object.keys(validationSchema).map((path) => [path, defineField(path)[0]]));
const expectedPerWeek = data.work_schedule.expected_per_week;

const hasLimit = computed(() => [values.global, ...values.members].some((row) => LIMIT_FIELDS.some((field) => !blank(row[field]))));
const saveHint = computed(() => {
  if (!meta.value.valid) {return t('fix_errors');}

  return hasLimit.value ? null : t('save_hint');
});
const summaryColumns = computed(() => [
  { key: 'name', label: t('member'), ...SUMMARY_COLUMN },
  ...LIMIT_FIELDS.map((field) => ({ key: field, label: t(field), ...SUMMARY_VALUE_COLUMN, width: 'w-1/4' })),
]);
const limitCells = (row, emptyText) => Object.fromEntries(LIMIT_FIELDS.map((field) => [field, {
  text: blank(row[field]) ? emptyText : t('hours', { value: row[field] }),
  muted: blank(row[field]),
}]));
const summaryRows = computed(() => [
  { id: 'all', name: t('all_members'), ...limitCells(data.global_limits, t('no_limit')) },
  ...data.members.map((member) => ({ ...member, ...limitCells(member, t('org_wide')) })),
]);

const save = handleSubmit(({ global, members }) => run(async () => {
  await OrganizationsResource.updateWorkLimitsAndExpectations(
    { id: organization.id },
    {
      work_limits_and_expectations: {
        ...data.work_schedule,
        ...global,
        expected_per_week: blank(expectedPerWeek) || blank(global.weekly_limit) ? expectedPerWeek : Math.min(expectedPerWeek, Number(global.weekly_limit)),
      },
    },
    { abortReload: true },
  );
  const edits = members.flatMap((member, index) => {
    const changedFields = LIMIT_FIELDS.filter((field) => String(member[field] ?? '') !== String(data.members[index][field] ?? ''));

    return changedFields.length ? [{ member, changedFields }] : [];
  });
  const results = await Promise.allSettled(edits.map(({ member: { id, weekly_limit, daily_limit }, changedFields }) => {
    const limits = { weekly_limit: blank(weekly_limit) ? null : weekly_limit, daily_limit: blank(daily_limit) ? null : daily_limit };
    const expected = limits.weekly_limit && changedFields.includes('weekly_limit') && { expected_per_week: limits.weekly_limit };

    return UserOrganizationsResource.update(
      { id },
      { user_organization: { limits: { ...limits, ...expected } } },
      { abortReload: true },
    );
  }));

  const errors = failures(results);

  return errors.length ? errors : finishStep(organization.id, 'set_up_time_limits');
}));
</script>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "description": "Cap how many hours members can track.",
    "global_limits": "Global limits",
    "weekly_limit": "Weekly limit",
    "daily_limit": "Daily limit",
    "weekly_limit_unit": "hours / week",
    "daily_limit_unit": "hours / day",
    "all_members": "All members",
    "info": "Members can also request more time above their limit. If a user has no set limit, they will use the global limit.",
    "individual_limits": "Individual limits",
    "member_limit": "{limit} for {name}",
    "member": "Member",
    "org_wide": "Org-wide",
    "no_limit": "No limit",
    "hours": "{value} h",
    "save_limits": "Save limits",
    "save_hint": "Set at least one limit first.",
    "edit": "Edit member limits"
  }
}
</i18n>
